import React, { useState, useEffect, useRef } from "react";
import { supabase } from "./supabaseClient";
import { API_BASE_URL, COURSE_DETAILS_FILE, SURVEY_CONFIG } from "./config";
import { GraduationCap, Sparkles, CheckCircle2, ChevronRight, ChevronLeft, Search, X, Loader2, XCircle } from "lucide-react";
import Button from "./components/ui/Button";
import { useNavigate } from "react-router-dom";

// Option Constants
const AGREEMENT_SCALE = [
  "Strongly disagree",
  "Disagree",
  "Somewhat disagree",
  "Neither agree nor disagree",
  "Somewhat agree",
  "Agree",
  "Strongly agree"
];

const GRIT_SCALE = [
  "Not like me at all",
  "Not much like me",
  "Somewhat like me",
  "Mostly like me",
  "Very much like me"
];

const FEELINGS_SCALE = [
  "Not at all",
  "Slightly",
  "Moderately",
  "Very",
  "Extremely"
];

const SATISFACTION_SCALE = [
  "Very Satisfied",
  "Satisfied",
  "Neither Satisfied or Disappointed",
  "Disappointed",
  "Very Disappointed"
];

const GENDER_OPTIONS = [
  "Man",
  "Woman",
  "Non-binary",
  "Prefer to self-describe",
  "Prefer not to answer"
];

const HISPANIC_OPTIONS = [
  "Yes",
  "No",
  "Prefer not to answer"
];

const RACE_OPTIONS = [
  "White",
  "Black or African American",
  "Asian",
  "American Indian or Alaska Native",
  "Native Hawaiian or Other Pacific Islander",
  "Middle Eastern or North African",
  "Prefer to self-describe",
  "Prefer not to answer"
];

const REASONS_OPTIONS = [
  "I think it will help me get a job or internship in the future.",
  "It helps me develop a specific skill I want to improve.",
  "It looked intellectually interesting to me.",
  "My parents or family encouraged me to take it.",
  "My friends are also taking this class.",
  "It fits well with my schedule (timing, workload, etc.).",
  "I heard good things about the professor/instructor.",
  "I heard it was an easier course.",
  "Other"
];

export default function BaselineSurvey({ user, userGroup, onComplete, alreadyCompleted = false, priorEnrolledCourses = [] }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(alreadyCompleted ? 99 : 0);
  const [catalog, setCatalog] = useState([]);
  const [isLoadingCatalog, setIsLoadingCatalog] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [courseSuggestions, setCourseSuggestions] = useState([]);
  const [isGeneratingRecs, setIsGeneratingRecs] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // --- INFORMED CONSENT STATE ---
  const [consentName, setConsentName] = useState("");
  const [rejectedConsent, setRejectedConsent] = useState(false);

  // --- CONFIG 1: DEMOGRAPHICS STATE ---
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("");
  const [genderOther, setGenderOther] = useState("");
  const [hispanic, setHispanic] = useState("");
  const [race, setRace] = useState([]);
  const [raceOther, setRaceOther] = useState("");
  const [residenceType, setResidenceType] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [foreignResidence, setForeignResidence] = useState("");

  // --- ACADEMIC GOALS STATE (ALL CONFIGS) ---
  const [academicGoalsAgree, setAcademicGoalsAgree] = useState("");
  const [academicGoalsOpen, setAcademicGoalsOpen] = useState("");

  // --- FIELD EVALUATIONS STATE (ALL CONFIGS) ---
  const [humanitiesServe, setHumanitiesServe] = useState("");
  const [humanitiesPrepared, setHumanitiesPrepared] = useState("");
  const [artsServe, setArtsServe] = useState("");
  const [artsPrepared, setArtsPrepared] = useState("");
  const [socialSciencesServe, setSocialSciencesServe] = useState("");
  const [socialSciencesPrepared, setSocialSciencesPrepared] = useState("");
  const [naturalSciencesServe, setNaturalSciencesServe] = useState("");
  const [naturalSciencesPrepared, setNaturalSciencesPrepared] = useState("");

  // --- GRIT STATE (CONFIGS 1, 3, 4) ---
  const [finishWhateverBegin, setFinishWhateverBegin] = useState("");
  const [setbacksDontDiscourage, setSetbacksDontDiscourage] = useState("");
  const [diligent, setDiligent] = useState("");
  const [hardWorker, setHardWorker] = useState("");

  // --- ATTITUDES STATE (ALL CONFIGS) ---
  const [challengesPrefer, setChallengesPrefer] = useState("");
  const [understandDeeply, setUnderstandDeeply] = useState("");
  const [showAbility, setShowAbility] = useState("");
  const [goodGrade, setGoodGrade] = useState("");

  // --- FEELINGS STATE (CONFIGS 1, 3, 4) ---
  const [excitement, setExcitement] = useState("");
  const [anxiety, setAnxiety] = useState("");
  const [motivation, setMotivation] = useState("");
  const [uncertainty, setUncertainty] = useState("");

  // --- CONFIG 1: COURSES THINKING ABOUT STATE ---
  const [coursesThinkingAbout, setCoursesThinkingAbout] = useState("");

  // --- CONFIGS 2, 3, 4: ENROLLED/PREREGISTERED COURSES + REASONS STATE ---
  // List of objects: { code, title, reasons: [], otherReason: "" }
  const [enrolledCourses, setEnrolledCourses] = useState([]);

  // --- CONFIGS 3, 4: COURSE SATISFACTION STATE ---
  // Map of course_code -> rating
  const [fallCourseSatisfaction, setFallCourseSatisfaction] = useState({});

  // --- CONFIGS 2, 3, 4: SURPRISE RECOMMENDATIONS STATE ---
  const [surpriseCourses, setSurpriseCourses] = useState([]);
  const [surpriseEvaluation, setSurpriseEvaluation] = useState({
    course1: "",
    course2: "",
    course3: ""
  });

  const searchInputRef = useRef(null);

  // Load course catalog for Dropdown Search
  useEffect(() => {
    if (alreadyCompleted) return;
    const fetchCatalog = async () => {
      setIsLoadingCatalog(true);
      try {
        const response = await fetch(`/${COURSE_DETAILS_FILE}`);
        if (response.ok) {
          const data = await response.json();
          const currentSemCourses = data.filter(c => c.semester === "2627F");
          setCatalog(currentSemCourses);
        }
      } catch (err) {
        console.error("Error loading course catalog:", err);
      } finally {
        setIsLoadingCatalog(false);
      }
    };
    fetchCatalog();
  }, [alreadyCompleted]);

  // Pre-fill satisfaction courses from prior enrolled courses if available (Config 3 & 4)
  useEffect(() => {
    if (priorEnrolledCourses && priorEnrolledCourses.length > 0) {
      const initialMap = {};
      priorEnrolledCourses.forEach(c => {
        const code = typeof c === "string" ? c : (c.code || (c.course_codes ? c.course_codes[0] : ""));
        if (code) initialMap[code] = "";
      });
      setFallCourseSatisfaction(prev => ({ ...initialMap, ...prev }));
    }
  }, [priorEnrolledCourses]);

  // Handle autocomplete course search
  useEffect(() => {
    if (!searchQuery.trim() || catalog.length === 0) {
      setCourseSuggestions([]);
      return;
    }
    const query = searchQuery.toLowerCase().trim();
    const matches = catalog.filter(course => {
      const codes = Array.isArray(course.course_codes) ? course.course_codes : [course.course_codes];
      const codeMatch = codes.some(code => code.toLowerCase().includes(query));
      const titleMatch = course.course_title && course.course_title.toLowerCase().includes(query);
      return codeMatch || titleMatch;
    }).slice(0, 10);
    setCourseSuggestions(matches);
  }, [searchQuery, catalog]);

  // Handle fetching surprise recommendations
  const fetchSurpriseRecommendations = async () => {
    setIsGeneratingRecs(true);
    setErrorMsg("");
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error("No session token");

      const courseCodesToSend = enrolledCourses.map(c => c.code);

      const response = await fetch(`${API_BASE_URL}/survey_surprise_recommendations`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ selected_courses: courseCodesToSend })
      });

      if (!response.ok) {
        throw new Error("Failed to load recommendations");
      }

      const data = await response.json();
      setSurpriseCourses(data);
    } catch (err) {
      console.error("Error fetching surprise recommendations:", err);
      setErrorMsg("Failed to generate recommendations. Proceeding to next section.");
    } finally {
      setIsGeneratingRecs(false);
    }
  };

  // Helper to determine the active field of study index based on current step
  const getActiveFieldIdx = () => {
    if (SURVEY_CONFIG === "1" || SURVEY_CONFIG === "3" || SURVEY_CONFIG === "4") {
      if (step >= 3 && step <= 6) {
        return step - 3;
      }
    } else if (SURVEY_CONFIG === "2") {
      if (step >= 2 && step <= 5) {
        return step - 2;
      }
    }
    return -1;
  };

  // Step names per configuration
  const getStepsCount = () => {
    if (SURVEY_CONFIG === "1") return 11; // 0: intro, 1: demo, 2: goals, 3: hum, 4: art, 5: soc, 6: stem, 7: grit, 8: attitudes, 9: feelings, 10: thinking, 11: complete
    if (SURVEY_CONFIG === "2") return 9;  // 0: intro, 1: goals, 2: hum, 3: art, 4: soc, 5: stem, 6: enrolled, 7: surprise, 8: attitudes, 9: complete
    if (SURVEY_CONFIG === "3") return 12; // 0: intro, 1: sat, 2: goals, 3: hum, 4: art, 5: soc, 6: stem, 7: grit, 8: attitudes, 9: feelings, 10: enrolled, 11: surprise, 12: complete
    if (SURVEY_CONFIG === "4") return 12; // 0: intro, 1: sat, 2: goals, 3: hum, 4: art, 5: soc, 6: stem, 7: grit, 8: attitudes, 9: feelings, 10: prereg, 11: surprise, 12: complete
    return 11;
  };

  const totalSteps = getStepsCount();

  const handleRejectConsent = () => {
    if (userGroup === "project") {
      navigate("/", { replace: true });
    } else {
      setRejectedConsent(true);
    }
  };

  const handleNextStep = async () => {
    setErrorMsg("");

    if (step === 0) {
      if (!consentName.trim()) {
        setErrorMsg("Please write down your full name to give consent and proceed.");
        return;
      }
    }

    // Configuration 1 Validation
    if (SURVEY_CONFIG === "1") {
      if (step === 1) {
        if (!age || !gender || !hispanic || race.length === 0 || !residenceType) {
          setErrorMsg("Please answer all demographic questions before proceeding.");
          return;
        }
        if (residenceType === "In the United States" && !zipCode.trim()) {
          setErrorMsg("Please enter your last US zip code of residence.");
          return;
        }
        if (residenceType === "Outside of the United States" && !foreignResidence.trim()) {
          setErrorMsg("Please enter your city and country of residence.");
          return;
        }
      } else if (step === 2) {
        if (!academicGoalsAgree || !academicGoalsOpen.trim()) {
          setErrorMsg("Please answer all academic goals questions before proceeding.");
          return;
        }
      } else if (step === 3) {
        if (!humanitiesServe || !humanitiesPrepared) {
          setErrorMsg("Please evaluate Humanities before proceeding.");
          return;
        }
      } else if (step === 4) {
        if (!artsServe || !artsPrepared) {
          setErrorMsg("Please evaluate Arts before proceeding.");
          return;
        }
      } else if (step === 5) {
        if (!socialSciencesServe || !socialSciencesPrepared) {
          setErrorMsg("Please evaluate Social Sciences before proceeding.");
          return;
        }
      } else if (step === 6) {
        if (!naturalSciencesServe || !naturalSciencesPrepared) {
          setErrorMsg("Please evaluate STEM / Natural Sciences before proceeding.");
          return;
        }
      } else if (step === 7) {
        if (!finishWhateverBegin || !setbacksDontDiscourage || !diligent || !hardWorker) {
          setErrorMsg("Please answer all descriptions of yourself before proceeding.");
          return;
        }
      } else if (step === 8) {
        if (!challengesPrefer || !understandDeeply || !showAbility || !goodGrade) {
          setErrorMsg("Please answer all statements about your learning goals before proceeding.");
          return;
        }
      } else if (step === 9) {
        if (!excitement || !anxiety || !motivation || !uncertainty) {
          setErrorMsg("Please rate all feelings before proceeding.");
          return;
        }
      } else if (step === 10) {
        if (!coursesThinkingAbout.trim()) {
          setErrorMsg("Please briefly describe the courses you are thinking about taking.");
          return;
        }
        await handleSubmitSurvey();
        return;
      }
    }

    // Configuration 2 Validation
    else if (SURVEY_CONFIG === "2") {
      if (step === 1) {
        if (!academicGoalsAgree || !academicGoalsOpen.trim()) {
          setErrorMsg("Please answer all academic goals questions before proceeding.");
          return;
        }
      } else if (step === 2) {
        if (!humanitiesServe || !humanitiesPrepared) {
          setErrorMsg("Please evaluate Humanities before proceeding.");
          return;
        }
      } else if (step === 3) {
        if (!artsServe || !artsPrepared) {
          setErrorMsg("Please evaluate Arts before proceeding.");
          return;
        }
      } else if (step === 4) {
        if (!socialSciencesServe || !socialSciencesPrepared) {
          setErrorMsg("Please evaluate Social Sciences before proceeding.");
          return;
        }
      } else if (step === 5) {
        if (!naturalSciencesServe || !naturalSciencesPrepared) {
          setErrorMsg("Please evaluate STEM / Natural Sciences before proceeding.");
          return;
        }
      } else if (step === 6) {
        if (enrolledCourses.length === 0) {
          setErrorMsg("Please select at least one course you are enrolled in.");
          return;
        }
        setStep(7);
        fetchSurpriseRecommendations();
        return;
      } else if (step === 7) {
        if (!surpriseEvaluation.course1 || !surpriseEvaluation.course2 || !surpriseEvaluation.course3) {
          setErrorMsg("Please answer whether you came across each of the 3 recommended courses.");
          return;
        }
      } else if (step === 8) {
        if (!challengesPrefer || !understandDeeply || !showAbility || !goodGrade) {
          setErrorMsg("Please answer all statements before proceeding.");
          return;
        }
        await handleSubmitSurvey();
        return;
      }
    }

    // Configuration 3 Validation (Midline II)
    else if (SURVEY_CONFIG === "3") {
      if (step === 1) {
        const keys = Object.keys(fallCourseSatisfaction);
        if (keys.length > 0 && keys.some(k => !fallCourseSatisfaction[k])) {
          setErrorMsg("Please rate your satisfaction for all listed fall courses.");
          return;
        }
      } else if (step === 2) {
        if (!academicGoalsAgree || !academicGoalsOpen.trim()) {
          setErrorMsg("Please answer all academic goals questions before proceeding.");
          return;
        }
      } else if (step === 3) {
        if (!humanitiesServe || !humanitiesPrepared) {
          setErrorMsg("Please evaluate Humanities before proceeding.");
          return;
        }
      } else if (step === 4) {
        if (!artsServe || !artsPrepared) {
          setErrorMsg("Please evaluate Arts before proceeding.");
          return;
        }
      } else if (step === 5) {
        if (!socialSciencesServe || !socialSciencesPrepared) {
          setErrorMsg("Please evaluate Social Sciences before proceeding.");
          return;
        }
      } else if (step === 6) {
        if (!naturalSciencesServe || !naturalSciencesPrepared) {
          setErrorMsg("Please evaluate STEM / Natural Sciences before proceeding.");
          return;
        }
      } else if (step === 7) {
        if (!finishWhateverBegin || !setbacksDontDiscourage || !diligent || !hardWorker) {
          setErrorMsg("Please answer all statements regarding your past semester before proceeding.");
          return;
        }
      } else if (step === 8) {
        if (!challengesPrefer || !understandDeeply || !showAbility || !goodGrade) {
          setErrorMsg("Please answer all statements before proceeding.");
          return;
        }
      } else if (step === 9) {
        if (!excitement || !anxiety || !motivation || !uncertainty) {
          setErrorMsg("Please rate all feelings before proceeding.");
          return;
        }
      } else if (step === 10) {
        if (enrolledCourses.length === 0) {
          setErrorMsg("Please select at least one course you are enrolled in.");
          return;
        }
        setStep(11);
        fetchSurpriseRecommendations();
        return;
      } else if (step === 11) {
        if (!surpriseEvaluation.course1 || !surpriseEvaluation.course2 || !surpriseEvaluation.course3) {
          setErrorMsg("Please answer whether you came across each of the 3 recommended courses.");
          return;
        }
        await handleSubmitSurvey();
        return;
      }
    }

    // Configuration 4 Validation (Endline)
    else if (SURVEY_CONFIG === "4") {
      if (step === 1) {
        const keys = Object.keys(fallCourseSatisfaction);
        if (keys.length > 0 && keys.some(k => !fallCourseSatisfaction[k])) {
          setErrorMsg("Please rate your satisfaction for all listed courses.");
          return;
        }
      } else if (step === 2) {
        if (!academicGoalsAgree || !academicGoalsOpen.trim()) {
          setErrorMsg("Please answer all academic goals questions before proceeding.");
          return;
        }
      } else if (step === 3) {
        if (!humanitiesServe || !humanitiesPrepared) {
          setErrorMsg("Please evaluate Humanities before proceeding.");
          return;
        }
      } else if (step === 4) {
        if (!artsServe || !artsPrepared) {
          setErrorMsg("Please evaluate Arts before proceeding.");
          return;
        }
      } else if (step === 5) {
        if (!socialSciencesServe || !socialSciencesPrepared) {
          setErrorMsg("Please evaluate Social Sciences before proceeding.");
          return;
        }
      } else if (step === 6) {
        if (!naturalSciencesServe || !naturalSciencesPrepared) {
          setErrorMsg("Please evaluate STEM / Natural Sciences before proceeding.");
          return;
        }
      } else if (step === 7) {
        if (!finishWhateverBegin || !setbacksDontDiscourage || !diligent || !hardWorker) {
          setErrorMsg("Please answer all statements regarding your past semester before proceeding.");
          return;
        }
      } else if (step === 8) {
        if (!challengesPrefer || !understandDeeply || !showAbility || !goodGrade) {
          setErrorMsg("Please answer all statements before proceeding.");
          return;
        }
      } else if (step === 9) {
        if (!excitement || !anxiety || !motivation || !uncertainty) {
          setErrorMsg("Please rate all feelings before proceeding.");
          return;
        }
      } else if (step === 10) {
        if (enrolledCourses.length === 0) {
          setErrorMsg("Please select at least one course you are preregistered for.");
          return;
        }
        setStep(11);
        fetchSurpriseRecommendations();
        return;
      } else if (step === 11) {
        if (!surpriseEvaluation.course1 || !surpriseEvaluation.course2 || !surpriseEvaluation.course3) {
          setErrorMsg("Please answer whether you came across each of the 3 recommended courses.");
          return;
        }
        await handleSubmitSurvey();
        return;
      }
    }

    setStep(prev => prev + 1);
  };

  const handlePrevStep = () => {
    setErrorMsg("");
    setStep(prev => Math.max(0, prev - 1));
  };

  const handleSubmitSurvey = async () => {
    setIsSubmitting(true);
    setErrorMsg("");

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error("No session token found.");

      const payload = {
        survey_config: SURVEY_CONFIG,
        user_group: userGroup || "control",
        academic_goals_agree: academicGoalsAgree,
        academic_goals_open: academicGoalsOpen,
        humanities_serve: humanitiesServe,
        humanities_prepared: humanitiesPrepared,
        arts_serve: artsServe,
        arts_prepared: artsPrepared,
        social_sciences_serve: socialSciencesServe,
        social_sciences_prepared: socialSciencesPrepared,
        natural_sciences_serve: naturalSciencesServe,
        natural_sciences_prepared: naturalSciencesPrepared,
        challenges_prefer: challengesPrefer,
        understand_deeply: understandDeeply,
        show_ability: showAbility,
        good_grade: goodGrade
      };

      if (SURVEY_CONFIG === "1") {
        Object.assign(payload, {
          age: age ? parseInt(age, 10) : null,
          gender: gender === "Prefer to self-describe" ? (genderOther.trim() ? `Self-described: ${genderOther.trim()}` : gender) : gender,
          hispanic,
          race: race.map(r => r === "Prefer to self-describe" ? (raceOther.trim() ? `Self-described: ${raceOther.trim()}` : r) : r),
          residence_type: residenceType,
          zip_code: zipCode,
          foreign_residence: foreignResidence,
          finish_whatever_begin: finishWhateverBegin,
          setbacks_dont_discourage: setbacksDontDiscourage,
          diligent,
          hard_worker: hardWorker,
          excitement,
          anxiety,
          motivation,
          uncertainty,
          courses_thinking_about: coursesThinkingAbout
        });
      } else if (SURVEY_CONFIG === "2") {
        Object.assign(payload, {
          enrolled_courses: enrolledCourses,
          surprise_courses: surpriseCourses,
          surprise_evaluation: surpriseEvaluation
        });
      } else if (SURVEY_CONFIG === "3") {
        Object.assign(payload, {
          fall_course_satisfaction: fallCourseSatisfaction,
          finish_whatever_begin: finishWhateverBegin,
          setbacks_dont_discourage: setbacksDontDiscourage,
          diligent,
          hard_worker: hardWorker,
          excitement,
          anxiety,
          motivation,
          uncertainty,
          enrolled_courses: enrolledCourses,
          surprise_courses: surpriseCourses,
          surprise_evaluation: surpriseEvaluation
        });
      } else if (SURVEY_CONFIG === "4") {
        Object.assign(payload, {
          prior_course_satisfaction: fallCourseSatisfaction,
          finish_whatever_begin: finishWhateverBegin,
          setbacks_dont_discourage: setbacksDontDiscourage,
          diligent,
          hard_worker: hardWorker,
          excitement,
          anxiety,
          motivation,
          uncertainty,
          preregistered_courses: enrolledCourses,
          surprise_courses: surpriseCourses,
          surprise_evaluation: surpriseEvaluation
        });
      }

      const response = await fetch(`${API_BASE_URL}/submit_survey`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || errorData.error || "Failed to submit survey");
      }

      onComplete();
      setStep(totalSteps); // Jump to completion screen
    } catch (err) {
      console.error("Error submitting survey:", err);
      setErrorMsg(err.message || "Failed to submit survey. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handlers for Enrolled / Preregistered Courses
  const handleAddCourse = (course) => {
    if (enrolledCourses.length >= 6) {
      setErrorMsg("You can add up to 6 courses.");
      return;
    }
    const code = Array.isArray(course.course_codes) ? course.course_codes[0] : course.course_codes;
    if (enrolledCourses.some(c => c.code === code)) {
      setSearchQuery("");
      setCourseSuggestions([]);
      return;
    }

    const newCourseObj = {
      code,
      title: course.course_title,
      reasons: [],
      otherReason: ""
    };
    setEnrolledCourses(prev => [...prev, newCourseObj]);
    setSearchQuery("");
    setCourseSuggestions([]);
  };

  const handleRemoveCourse = (code) => {
    setEnrolledCourses(prev => prev.filter(c => c.code !== code));
  };

  const handleToggleReason = (courseCode, reason) => {
    setEnrolledCourses(prev => prev.map(c => {
      if (c.code !== courseCode) return c;
      const currentReasons = c.reasons || [];
      if (currentReasons.includes(reason)) {
        return { ...c, reasons: currentReasons.filter(r => r !== reason) };
      } else {
        if (currentReasons.length >= 3) return c; // Max top 3
        return { ...c, reasons: [...currentReasons, reason] };
      }
    }));
  };

  const handleOtherReasonChange = (courseCode, text) => {
    setEnrolledCourses(prev => prev.map(c => c.code === courseCode ? { ...c, otherReason: text } : c));
  };

  // --- RENDER DECLINED SCREEN ---
  if (rejectedConsent) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas p-4 sm:p-6 lg:p-8">
        <div className="card max-w-xl p-8 sm:p-10 space-y-6 text-center shadow-lg border border-line">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-warning-soft text-warning">
            <XCircle className="h-8 w-8" />
          </div>
          <h1 className="font-display text-3xl font-bold text-ink">Consent Declined</h1>
          <p className="text-sm text-ink-muted leading-relaxed">
            You have chosen not to participate in the study. Since your account is in the survey-only group, access to the interactive course map is locked. You can safely close this page or log out.
          </p>
          <div className="flex flex-col gap-3 mt-4">
            <Button variant="secondary" className="w-full py-3" onClick={async () => {
              await supabase.auth.signOut();
              window.location.reload();
            }}>
              Log Out
            </Button>
            <button
              type="button"
              className="text-xs text-primary hover:underline font-medium"
              onClick={() => setRejectedConsent(false)}
            >
              Go Back to Consent Form
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- RENDER COMPLETION SCREEN ---
  if (step >= totalSteps || alreadyCompleted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas p-4 sm:p-6 lg:p-8">
        <div className="card max-w-xl p-8 sm:p-10 space-y-6 text-center shadow-lg border border-line">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-success-soft text-success">
            <CheckCircle2 className="h-8 w-8" />
          </div>
          <h1 className="font-display text-3xl font-bold text-ink">Thank You!</h1>
          <p className="text-sm text-ink-muted leading-relaxed">
            You have successfully completed the survey. Your feedback is incredibly valuable to our research on open curriculum advising.
          </p>

          {userGroup === "project" ? (
            <div className="border-t border-line/60 pt-6 space-y-4">
              <p className="text-xs text-ink-soft">
                As a member of the Project group, you now have full access to explore the interactive course catalog map.
              </p>
              <Button variant="primary" className="w-full py-3.5 text-base" onClick={() => { onComplete(); navigate('/', { replace: true }); }}>
                Explore the Course Map <Sparkles className="h-5 w-5 ml-1" />
              </Button>
            </div>
          ) : (
            <div className="border-t border-line/60 pt-6 bg-surface-alt/40 rounded-xl p-4 text-xs text-ink-muted leading-relaxed">
              <strong>Study Participant Notice:</strong> You have completed the requirements for the study. You can safely close this page or log out.
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-canvas py-8 px-4 sm:px-6 lg:px-8 overflow-y-auto">
      <div className="max-w-4xl w-full mx-auto flex flex-col h-full justify-between">
        
        {/* Header & Step progress bar */}
        <div className="mb-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary text-white">
                <GraduationCap className="h-4 w-4" />
              </span>
              <span className="font-display font-semibold text-ink text-sm">
                {SURVEY_CONFIG === "1" && "Baseline Survey"}
                {SURVEY_CONFIG === "2" && "Midline Survey I"}
                {SURVEY_CONFIG === "3" && "Midline Survey II"}
                {SURVEY_CONFIG === "4" && "Endline Survey"}
              </span>
              {userGroup === "project" && (
                <button
                  type="button"
                  onClick={() => navigate("/")}
                  className="text-xs text-primary hover:underline font-medium ml-2 border-l border-line pl-2"
                >
                  Back to Map
                </button>
              )}
            </div>
            <span className="text-xs text-ink-muted font-medium">
              Step {step} of {totalSteps - 1}
            </span>
          </div>

          <div className="w-full bg-surface-alt h-2 rounded-full overflow-hidden">
            <div 
              className="bg-primary h-full transition-all duration-300 ease-out" 
              style={{ width: `${(step / (totalSteps - 1)) * 100}%` }}
            />
          </div>
        </div>

        {/* Form Body */}
        <div className="card p-6 sm:p-8 space-y-8 my-auto shadow-md border border-line">
          {errorMsg && (
            <div className="p-4 rounded-xl bg-danger-soft text-danger text-xs font-medium border border-danger/20">
              {errorMsg}
            </div>
          )}

          {/* STEP 0: INTRO / INFORMED CONSENT */}
          {step === 0 && (
            <div className="space-y-6 text-left max-w-2xl mx-auto py-2">
              <div className="border-b border-line pb-4 text-center">
                <h1 className="font-display text-2xl sm:text-3xl font-bold text-ink">
                  Informed Consent Form
                </h1>
                <p className="text-xs text-ink-muted mt-1">Open Curriculum Study at Amherst College</p>
              </div>

              <div className="space-y-4 text-sm text-ink-muted leading-relaxed max-h-[350px] overflow-y-auto pr-2 border border-line/40 p-4 rounded-xl bg-surface-alt/10">
                <p>
                  We are a group of alumni supporting Amherst College to improve their academic advising practices. This short survey asks about your goals, interests, and feelings as you begin at Amherst. Your answers will help Amherst College understand your course selection experiences and improve upon its processes.
                </p>
                <p className="font-medium text-ink">
                  There are no right or wrong answers. Please respond honestly – your answers will NOT be shared with anyone but the research team, and will NOT affect your grades or course placements!
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-line/40 text-xs">
                  <div>
                    <span className="font-semibold text-ink">Research Team:</span>
                    <p>Ariana Ravitch, Harufumi Nakazawa, Aryen Shrestha, Sebastien Brown</p>
                  </div>
                  <div>
                    <span className="font-semibold text-ink">Estimated Completion Time:</span>
                    <p>5-10 minutes</p>
                  </div>
                </div>

                <p className="text-xs pt-2 border-t border-line/40">
                  This study has been approved by the Institutional Review Board for the Protection of Human Subjects at Amherst College. We believe there is very little risk in you being part of the study. However, if you feel uncomfortable answering any survey questions, you can choose not to answer any question or stop the survey at any time, even if you already agreed to participate.
                  If you have any questions regarding the study at any point, please contact: <a href="mailto:research@courstellation.org" className="text-primary hover:underline font-medium">research@courstellation.org</a>.
                </p>

                <div className="space-y-2 pt-3 border-t border-line/40">
                  <p className="font-semibold text-ink">I understand that:</p>
                  <ol className="list-decimal pl-5 space-y-1 text-xs">
                    <li>My participation is voluntary.</li>
                    <li>I may withdraw my consent and discontinue participation in this study (or any portion thereof) at any time without bearing any negative consequences.</li>
                    <li>I may be contacted by the research team for follow-up surveys during this academic year.</li>
                    <li>All of the information from this study will be strictly confidential. No names will be associated with the data in any way. I understand that the data will be stored in secure folders and will be accessible only to members of the researching group.</li>
                    <li>The results of this study will be made part of a final research report and may be used in papers submitted for publication or presented at professional conferences, but under no circumstances will my name or other identifying characteristics be included.</li>
                  </ol>
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t border-line">
                <p className="text-xs text-ink-soft leading-relaxed">
                  If you have reviewed the above and give consent to participate in this research, please write down your full name below and proceed to the next screen.
                </p>
                <div className="space-y-2">
                  <label htmlFor="consent-signature-input" className="block text-xs font-semibold text-ink">
                    Name:
                  </label>
                  <input
                    id="consent-signature-input"
                    type="text"
                    className="input-field w-full"
                    placeholder="Enter your full name to sign"
                    value={consentName}
                    onChange={e => setConsentName(e.target.value)}
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Button
                    variant="secondary"
                    className="flex-1 py-3"
                    onClick={handleRejectConsent}
                  >
                    Reject / Go Back
                  </Button>
                  <Button
                    variant="primary"
                    className="flex-1 py-3"
                    disabled={!consentName.trim()}
                    onClick={handleNextStep}
                  >
                    Accept &amp; Continue <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* DEMOGRAPHICS (CONFIG 1 ONLY, STEP 1) */}
          {SURVEY_CONFIG === "1" && step === 1 && (
            <div className="space-y-6">
              <div className="border-b border-line pb-4">
                <h2 className="text-xl font-bold text-ink">Demographic Information</h2>
                <p className="text-xs text-ink-muted mt-1">Please tell us a little bit about yourself.</p>
              </div>

              <div className="space-y-4">
                <label className="block text-sm font-medium text-ink">How old are you?</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="input-field w-full sm:max-w-sm py-3"
                  placeholder="e.g. 18"
                  value={age}
                  onChange={e => setAge(e.target.value.replace(/\D/g, ''))}
                />
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-medium text-ink">Which of the following best describes your gender identity?</label>
                <div className="space-y-2">
                  {GENDER_OPTIONS.map(opt => (
                    <label key={opt} className="flex items-center gap-3 p-3 rounded-xl border border-line hover:bg-surface-alt cursor-pointer transition">
                      <input
                        type="radio"
                        name="gender"
                        value={opt}
                        checked={gender === opt}
                        onChange={e => setGender(e.target.value)}
                        className="h-4 w-4 text-primary"
                      />
                      <span className="text-sm text-ink">{opt}</span>
                    </label>
                  ))}
                </div>
                {gender === "Prefer to self-describe" && (
                  <input
                    type="text"
                    className="input-field mt-2"
                    placeholder="Please specify"
                    value={genderOther}
                    onChange={e => setGenderOther(e.target.value)}
                  />
                )}
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-medium text-ink">Do you identify as Hispanic?</label>
                <div className="flex gap-4">
                  {HISPANIC_OPTIONS.map(opt => (
                    <label key={opt} className="flex items-center gap-2 p-3 rounded-xl border border-line hover:bg-surface-alt cursor-pointer transition flex-1 justify-center">
                      <input
                        type="radio"
                        name="hispanic"
                        value={opt}
                        checked={hispanic === opt}
                        onChange={e => setHispanic(e.target.value)}
                        className="h-4 w-4 text-primary"
                      />
                      <span className="text-sm text-ink">{opt}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-medium text-ink">Which of the following best describes your racial or ethnic identity? (Select all that apply)</label>
                <div className="space-y-2">
                  {RACE_OPTIONS.map(opt => (
                    <label key={opt} className="flex items-center gap-3 p-3 rounded-xl border border-line hover:bg-surface-alt cursor-pointer transition">
                      <input
                        type="checkbox"
                        checked={race.includes(opt)}
                        onChange={e => {
                          if (e.target.checked) setRace(prev => [...prev, opt]);
                          else setRace(prev => prev.filter(r => r !== opt));
                        }}
                        className="h-4 w-4 text-primary rounded"
                      />
                      <span className="text-sm text-ink">{opt}</span>
                    </label>
                  ))}
                </div>
                {race.includes("Prefer to self-describe") && (
                  <input
                    type="text"
                    className="input-field mt-2"
                    placeholder="Please specify your race/ethnicity"
                    value={raceOther}
                    onChange={e => setRaceOther(e.target.value)}
                  />
                )}
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-medium text-ink">Where did you last reside before starting Amherst?</label>
                <div className="space-y-2">
                  {["In the United States", "Outside of the United States"].map(opt => (
                    <label key={opt} className="flex items-center gap-3 p-3 rounded-xl border border-line hover:bg-surface-alt cursor-pointer transition">
                      <input
                        type="radio"
                        name="residenceType"
                        value={opt}
                        checked={residenceType === opt}
                        onChange={e => setResidenceType(e.target.value)}
                        className="h-4 w-4 text-primary"
                      />
                      <span className="text-sm text-ink">{opt}</span>
                    </label>
                  ))}
                </div>
                {residenceType === "In the United States" && (
                  <div className="pt-2">
                    <label className="block text-xs font-medium text-ink-muted mb-1">Please indicate your last zip code of residence:</label>
                    <input
                      type="text"
                      className="input-field w-full sm:max-w-sm py-3"
                      placeholder="e.g. 01002"
                      value={zipCode}
                      onChange={e => setZipCode(e.target.value)}
                    />
                  </div>
                )}
                {residenceType === "Outside of the United States" && (
                  <div className="pt-2">
                    <label className="block text-xs font-medium text-ink-muted mb-1">Please indicate your last city and country of residence:</label>
                    <input
                      type="text"
                      className="input-field"
                      placeholder="e.g. London, United Kingdom"
                      value={foreignResidence}
                      onChange={e => setForeignResidence(e.target.value)}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* COURSE SATISFACTION (CONFIG 3 & 4 STEP 1) */}
          {(SURVEY_CONFIG === "3" || SURVEY_CONFIG === "4") && step === 1 && (
            <div className="space-y-6">
              <div className="border-b border-line pb-4">
                <h2 className="text-xl font-bold text-ink">Course Satisfaction</h2>
                <p className="text-xs text-ink-muted mt-1">Please indicate your satisfaction with each course you were enrolled in during the previous semester.</p>
              </div>

              {Object.keys(fallCourseSatisfaction).length === 0 ? (
                <div className="p-4 rounded-xl bg-surface-alt text-xs text-ink-muted leading-relaxed">
                  No previous course records were found automatically. You may type course codes below to rate them:
                  <div className="mt-3 flex gap-2">
                    <input
                      type="text"
                      id="manualCourseInput"
                      className="input-field"
                      placeholder="e.g. MATH-111"
                    />
                    <Button variant="secondary" onClick={() => {
                      const input = document.getElementById("manualCourseInput");
                      if (input && input.value.trim()) {
                        const code = input.value.trim().toUpperCase();
                        setFallCourseSatisfaction(prev => ({ ...prev, [code]: "" }));
                        input.value = "";
                      }
                    }}>Add</Button>
                  </div>
                </div>
              ) : null}

              <div className="space-y-6">
                {Object.keys(fallCourseSatisfaction).map(code => (
                  <div key={code} className="p-4 rounded-xl border border-line space-y-3 bg-surface-alt/20">
                    <div className="font-semibold text-sm text-ink">{code}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
                      {SATISFACTION_SCALE.map(opt => (
                        <button
                          key={opt}
                          type="button"
                          className={`p-2 rounded-lg text-xs font-medium border text-center transition ${
                            fallCourseSatisfaction[code] === opt
                              ? "bg-primary text-white border-primary"
                              : "bg-surface border-line text-ink hover:bg-surface-alt"
                          }`}
                          onClick={() => setFallCourseSatisfaction(prev => ({ ...prev, [code]: opt }))}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ACADEMIC GOALS (CONFIG 1 STEP 2, CONFIG 2 STEP 1, CONFIG 3 STEP 2, CONFIG 4 STEP 2) */}
          {((SURVEY_CONFIG === "1" && step === 2) || (SURVEY_CONFIG === "2" && step === 1) || ((SURVEY_CONFIG === "3" || SURVEY_CONFIG === "4") && step === 2)) && (
            <div className="space-y-6">
              <div className="border-b border-line pb-4">
                <h2 className="text-xl font-bold text-ink">Academic Goals</h2>
                <p className="text-xs text-ink-muted mt-1">Reflect on your academic intentions for your time at Amherst College.</p>
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-medium text-ink">
                  To what extent do you agree with the following statement?
                </label>
                <p className="text-base font-semibold text-primary p-4 rounded-xl bg-primary-soft/30 border border-primary/20">
                  "I have clear academic goals for my time at Amherst College."
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-7 gap-2 pt-2">
                  {AGREEMENT_SCALE.map(opt => (
                    <button
                      key={opt}
                      type="button"
                      className={`p-3 rounded-xl border text-xs font-medium transition text-center flex flex-col justify-center items-center ${
                        academicGoalsAgree === opt
                          ? "bg-primary text-white border-primary shadow-sm"
                          : "bg-surface border-line text-ink hover:bg-surface-alt"
                      }`}
                      onClick={() => setAcademicGoalsAgree(opt)}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-3 pt-4 border-t border-line/60">
                <label className="block text-sm font-medium text-ink">
                  Please briefly describe your academic goals (a sentence or two is fine). If you don't have any yet, just write "none yet."
                </label>
                <textarea
                  rows={4}
                  className="input-field w-full p-3 text-sm"
                  placeholder="Describe your academic goals..."
                  value={academicGoalsOpen}
                  onChange={e => setAcademicGoalsOpen(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* FIELDS OF STUDY EVALUATIONS */}
          {getActiveFieldIdx() !== -1 && (() => {
            const fieldIdx = getActiveFieldIdx();
            const field = [
              { key: "humanities", label: "Humanities (e.g., English, philosophy, history, classics)", serve: humanitiesServe, setServe: setHumanitiesServe, prep: humanitiesPrepared, setPrep: setHumanitiesPrepared },
              { key: "arts", label: "Arts (e.g., music, theater and dance, art and the history of art)", serve: artsServe, setServe: setArtsServe, prep: artsPrepared, setPrep: setArtsPrepared },
              { key: "social", label: "Social Sciences (e.g., psychology, economics, political science, anthropology)", serve: socialSciencesServe, setServe: setSocialSciencesServe, prep: socialSciencesPrepared, setPrep: setSocialSciencesPrepared },
              { key: "stem", label: "STEM / Natural Sciences (e.g., biology, physics, math, computer science)", serve: naturalSciencesServe, setServe: setNaturalSciencesServe, prep: naturalSciencesPrepared, setPrep: setNaturalSciencesPrepared },
            ][fieldIdx];

            return (
              <div className="space-y-8">
                <div className="border-b border-line pb-4">
                  <h2 className="text-xl font-bold text-ink">Fields of Study</h2>
                  <p className="text-sm text-ink-muted mt-1">Evaluate how classes across different academic divisions align with your interests and preparation.</p>
                </div>

                <div key={field.key} className="p-5 rounded-2xl border border-line bg-surface-alt/20 space-y-5">
                  <h3 className="font-bold text-sm text-ink text-primary">{field.label}</h3>

                  {/* Serve question */}
                  <div className="space-y-2">
                    <p className="text-xs text-ink font-medium">Taking a class in this area would serve my interests, goals, and aspirations:</p>
                    <div className="grid grid-cols-1 sm:grid-cols-7 gap-1.5">
                      {AGREEMENT_SCALE.map(opt => (
                        <button
                          key={opt}
                          type="button"
                          className={`p-2 rounded-lg text-xs font-medium border text-center transition ${
                            field.serve === opt ? "bg-primary text-white border-primary" : "bg-surface border-line text-ink hover:bg-surface-alt"
                          }`}
                          onClick={() => field.setServe(opt)}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Prepared question */}
                  <div className="space-y-2 pt-2 border-t border-line/40">
                    <p className="text-xs text-ink font-medium">I feel well-prepared to take a class in this area:</p>
                    <div className="grid grid-cols-1 sm:grid-cols-7 gap-1.5">
                      {AGREEMENT_SCALE.map(opt => (
                        <button
                          key={opt}
                          type="button"
                          className={`p-2 rounded-lg text-xs font-medium border text-center transition ${
                            field.prep === opt ? "bg-primary text-white border-primary" : "bg-surface border-line text-ink hover:bg-surface-alt"
                          }`}
                          onClick={() => field.setPrep(opt)}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* GRIT / SELF-DESCRIPTION (CONFIG 1 STEP 7, CONFIG 3 STEP 7, CONFIG 4 STEP 7) */}
          {((SURVEY_CONFIG === "1" && step === 7) || ((SURVEY_CONFIG === "3" || SURVEY_CONFIG === "4") && step === 7)) && (
            <div className="space-y-6">
              <div className="border-b border-line pb-4">
                <h2 className="text-xl font-bold text-ink">Academic Self-Description</h2>
                <p className="text-sm text-ink-muted mt-1">
                  {SURVEY_CONFIG === "1" ? "How well do the following statements describe you academically?" : "How well did the following statements describe you in the last semester?"}
                </p>
              </div>

              {[
                { label: SURVEY_CONFIG === "1" ? "I finish whatever I begin." : "I finished whatever I began.", val: finishWhateverBegin, set: setFinishWhateverBegin },
                { label: SURVEY_CONFIG === "1" ? "Setbacks don't discourage me." : "Setbacks did not discourage me.", val: setbacksDontDiscourage, set: setSetbacksDontDiscourage },
                { label: SURVEY_CONFIG === "1" ? "I am diligent." : "I was diligent.", val: diligent, set: setDiligent },
                { label: SURVEY_CONFIG === "1" ? "I am a hard worker." : "I was a hard worker.", val: hardWorker, set: setHardWorker }
              ].map((item, idx) => (
                <div key={idx} className="p-4 rounded-xl border border-line space-y-3 bg-surface-alt/20">
                  <p className="text-sm font-semibold text-ink">{item.label}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
                    {GRIT_SCALE.map(opt => (
                      <button
                        key={opt}
                        type="button"
                        className={`p-2.5 rounded-lg text-xs font-medium border text-center transition ${
                          item.val === opt ? "bg-primary text-white border-primary" : "bg-surface border-line text-ink hover:bg-surface-alt"
                        }`}
                        onClick={() => item.set(opt)}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* COURSEWORK ATTITUDES (CONFIG 1 STEP 8, CONFIG 2 STEP 8, CONFIG 3 STEP 8, CONFIG 4 STEP 8) */}
          {((SURVEY_CONFIG === "1" && step === 8) || (SURVEY_CONFIG === "2" && step === 8) || ((SURVEY_CONFIG === "3" || SURVEY_CONFIG === "4") && step === 8)) && (
            <div className="space-y-6">
              <div className="border-b border-line pb-4">
                <h2 className="text-xl font-bold text-ink">Coursework & Learning Attitudes</h2>
                <p className="text-sm text-ink-muted mt-1">How well do the following statements describe your mindset toward coursework?</p>
              </div>

              {[
                { label: "I prefer coursework that challenges me, so I can learn new things.", val: challengesPrefer, set: setChallengesPrefer },
                { label: "Understanding course material deeply is more important to me than just getting through it.", val: understandDeeply, set: setUnderstandDeeply },
                { label: "I want to do well in coursework because it is important to show my ability to my family, friends, employers, or others.", val: showAbility, set: setShowAbility },
                { label: "Getting a good grade in a course is the most satisfying part of taking a course.", val: goodGrade, set: setGoodGrade }
              ].map((item, idx) => (
                <div key={idx} className="p-4 rounded-xl border border-line space-y-3 bg-surface-alt/20">
                  <p className="text-sm font-semibold text-ink">{item.label}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-7 gap-1.5">
                    {AGREEMENT_SCALE.map(opt => (
                      <button
                        key={opt}
                        type="button"
                        className={`p-2 rounded-lg text-xs font-medium border text-center transition ${
                          item.val === opt ? "bg-primary text-white border-primary" : "bg-surface border-line text-ink hover:bg-surface-alt"
                        }`}
                        onClick={() => item.set(opt)}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* FEELINGS (CONFIG 1 STEP 9, CONFIG 3 STEP 9, CONFIG 4 STEP 9) */}
          {((SURVEY_CONFIG === "1" && step === 9) || ((SURVEY_CONFIG === "3" || SURVEY_CONFIG === "4") && step === 9)) && (
            <div className="space-y-6">
              <div className="border-b border-line pb-4">
                <h2 className="text-xl font-bold text-ink">Feelings About Registration</h2>
                <p className="text-xs text-ink-muted mt-1">When you think about registering for courses, to what extent are you currently feeling each of the following?</p>
              </div>

              {[
                { label: "Excitement", val: excitement, set: setExcitement },
                { label: "Anxiety", val: anxiety, set: setAnxiety },
                { label: "Motivation", val: motivation, set: setMotivation },
                { label: "Uncertainty", val: uncertainty, set: setUncertainty }
              ].map((item) => (
                <div key={item.label} className="p-4 rounded-xl border border-line space-y-3 bg-surface-alt/20">
                  <p className="text-sm font-semibold text-ink">{item.label}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
                    {FEELINGS_SCALE.map(opt => (
                      <button
                        key={opt}
                        type="button"
                        className={`p-2.5 rounded-lg text-xs font-medium border text-center transition ${
                          item.val === opt ? "bg-primary text-white border-primary" : "bg-surface border-line text-ink hover:bg-surface-alt"
                        }`}
                        onClick={() => item.set(opt)}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* CONFIG 1 STEP 10: COURSES THINKING ABOUT */}
          {SURVEY_CONFIG === "1" && step === 10 && (
            <div className="space-y-6">
              <div className="border-b border-line pb-4">
                <h2 className="text-xl font-bold text-ink">Courses Under Consideration</h2>
                <p className="text-xs text-ink-muted mt-1">Tell us what courses you have in mind.</p>
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-medium text-ink">
                  Apart from your first year seminar, please briefly describe the courses you are thinking about taking. It can be general (e.g. “a course on creative writing”, “an intro to computer science”, “a required course for pre-med”) or specific course names.
                </label>
                <textarea
                  rows={5}
                  className="input-field w-full p-3 text-sm"
                  placeholder="e.g. Intro to Psych, a creative writing workshop, MATH-111..."
                  value={coursesThinkingAbout}
                  onChange={e => setCoursesThinkingAbout(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* CONFIGS 2, 3, 4 STEP: ENROLLED / PREREGISTERED COURSES + REASONS */}
          {((SURVEY_CONFIG === "2" && step === 6) || ((SURVEY_CONFIG === "3" || SURVEY_CONFIG === "4") && step === 10)) && (
            <div className="space-y-6">
              <div className="border-b border-line pb-4">
                <h2 className="text-xl font-bold text-ink">
                  {SURVEY_CONFIG === "4" ? "Preregistered Courses" : "Enrolled Courses"}
                </h2>
                <p className="text-xs text-ink-muted mt-1">
                  Please select up to 6 courses you are currently {SURVEY_CONFIG === "4" ? "preregistered for" : "enrolled in"} for the upcoming semester and indicate your top reasons for choosing each.
                </p>
              </div>

              {/* Course Search Input */}
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3.5 top-3.5 h-4 w-4 text-ink-muted" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    className="input-field pl-10 pr-4 py-3 text-sm w-full"
                    placeholder="Search by course code or title (e.g. MATH-111, Organic Chemistry)..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    disabled={enrolledCourses.length >= 6}
                  />
                  {isLoadingCatalog && (
                    <Loader2 className="absolute right-3.5 top-3.5 h-4 w-4 text-primary animate-spin" />
                  )}
                </div>

                {/* Suggestions Dropdown */}
                {courseSuggestions.length > 0 && (
                  <div className="absolute z-20 w-full mt-1 bg-surface border border-line rounded-xl shadow-xl max-h-60 overflow-y-auto divide-y divide-line/60">
                    {courseSuggestions.map(course => {
                      const code = Array.isArray(course.course_codes) ? course.course_codes[0] : course.course_codes;
                      return (
                        <button
                          key={code}
                          type="button"
                          className="w-full text-left p-3 hover:bg-surface-alt flex items-center justify-between transition"
                          onClick={() => handleAddCourse(course)}
                        >
                          <div>
                            <span className="font-semibold text-sm text-ink">{code}</span>
                            <span className="text-xs text-ink-muted ml-2">{course.course_title}</span>
                          </div>
                          <span className="text-xs text-primary font-medium">+ Add</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Selected Courses List */}
              <div className="space-y-4 pt-2">
                {enrolledCourses.length === 0 ? (
                  <div className="p-6 text-center border-2 border-dashed border-line rounded-2xl text-xs text-ink-muted">
                    No courses selected yet. Use the search bar above to add up to 6 courses.
                  </div>
                ) : (
                  enrolledCourses.map(course => (
                    <div key={course.code} className="p-4 rounded-xl border border-line bg-surface-alt/30 space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-bold text-sm text-ink">{course.code}</span>
                          <span className="text-xs text-ink-muted ml-2">{course.title}</span>
                        </div>
                        <button
                          type="button"
                          className="text-danger hover:text-danger/80 p-1 text-xs font-medium flex items-center gap-1"
                          onClick={() => handleRemoveCourse(course.code)}
                        >
                          <X className="h-4 w-4" /> Remove
                        </button>
                      </div>

                      {/* Reasons Selection */}
                      <div className="pt-2 border-t border-line/40">
                        <label className="block text-xs font-semibold text-ink mb-2">
                          Indicate your main reasons (select top 3) for choosing this course:
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {REASONS_OPTIONS.map(reason => {
                            const isChecked = (course.reasons || []).includes(reason);
                            return (
                              <label key={reason} className={`flex items-start gap-2.5 p-2.5 rounded-lg border text-xs cursor-pointer transition ${
                                isChecked ? "bg-primary-soft/30 border-primary text-ink font-medium" : "bg-surface border-line text-ink-muted hover:bg-surface-alt"
                              }`}>
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => handleToggleReason(course.code, reason)}
                                  className="h-3.5 w-3.5 text-primary rounded mt-0.5"
                                />
                                <span>{reason}</span>
                              </label>
                            );
                          })}
                        </div>
                        {(course.reasons || []).includes("Other") && (
                          <input
                            type="text"
                            className="input-field text-xs mt-2 w-full"
                            placeholder="Please specify other reason..."
                            value={course.otherReason || ""}
                            onChange={e => handleOtherReasonChange(course.code, e.target.value)}
                          />
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* CONFIGS 2, 3, 4 STEP: SURPRISE COURSE EVALUATION */}
          {((SURVEY_CONFIG === "2" && step === 7) || ((SURVEY_CONFIG === "3" || SURVEY_CONFIG === "4") && step === 11)) && (
            <div className="space-y-6">
              <div className="border-b border-line pb-4">
                <h2 className="text-xl font-bold text-ink">Surprise Course Discovery</h2>
                <p className="text-xs text-ink-muted mt-1">Here are 3 courses from other academic departments generated based on your selections:</p>
              </div>

              {isGeneratingRecs ? (
                <div className="py-12 text-center space-y-3">
                  <Loader2 className="h-8 w-8 text-primary animate-spin mx-auto" />
                  <p className="text-sm font-medium text-ink-muted">Generating personalized surprise courses...</p>
                </div>
              ) : surpriseCourses.length === 0 ? (
                <div className="p-4 rounded-xl bg-surface-alt text-xs text-ink-muted">
                  No surprise courses generated. Click next to proceed.
                </div>
              ) : (
                <div className="space-y-6">
                  {surpriseCourses.map((course, idx) => {
                    const courseKey = `course${idx + 1}`;
                    const code = Array.isArray(course.course_codes) ? course.course_codes.join(" / ") : course.course_codes;
                    return (
                      <div key={idx} className="p-5 rounded-2xl border border-line bg-surface-alt/30 space-y-4 shadow-sm">
                        <div>
                          <span className="inline-block px-2.5 py-1 rounded-md bg-primary-soft text-primary font-bold text-xs mb-1">
                            Course {idx + 1}: {code}
                          </span>
                          <h3 className="font-bold text-base text-ink">{course.course_title}</h3>
                          <p className="text-xs text-ink-muted leading-relaxed mt-2 line-clamp-3">
                            {course.description || "No description available."}
                          </p>
                        </div>

                        <div className="pt-3 border-t border-line/40 space-y-2">
                          <p className="text-xs font-semibold text-ink">
                            Did you come across {code || `Course ${idx + 1}`} while choosing your original courses above?
                          </p>
                          <div className="flex gap-3">
                            {["Yes", "Maybe", "No"].map(opt => (
                              <button
                                key={opt}
                                type="button"
                                className={`flex-1 py-2 px-3 rounded-lg border text-xs font-medium transition text-center ${
                                  surpriseEvaluation[courseKey] === opt
                                    ? "bg-primary text-white border-primary shadow-sm"
                                    : "bg-surface border-line text-ink hover:bg-surface-alt"
                                }`}
                                onClick={() => setSurpriseEvaluation(prev => ({ ...prev, [courseKey]: opt }))}
                              >
                                {opt}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Navigation Controls */}
          {step > 0 && step < totalSteps && (
            <div className="flex items-center justify-between pt-6 border-t border-line/60">
              <Button
                variant="secondary"
                disabled={step === 1 || isSubmitting}
                onClick={handlePrevStep}
              >
                <ChevronLeft className="h-4 w-4 mr-1" /> Previous
              </Button>

              <Button
                variant="primary"
                disabled={isSubmitting || isGeneratingRecs}
                onClick={handleNextStep}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting...
                  </>
                ) : step === totalSteps - 1 ? (
                  "Submit Survey"
                ) : (
                  <>
                    Next <ChevronRight className="h-4 w-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
